import React from 'react';
import { Link } from 'react-router-dom';

const POLICIES = {
  'privacy-policy': {
    title: 'Privacy Policy',
    updated: 'February 2026',
    content: `
<h2>1. Information We Collect</h2>
<p>When you use Benz Eatery's website and ordering system, we may collect:</p>
<ul>
<li><strong>Order Information:</strong> Items ordered, table number, order preferences</li>
<li><strong>Contact Information:</strong> Email address (if provided for order tracking)</li>
<li><strong>Device Information:</strong> Browser type, device type, IP address for security purposes</li>
<li><strong>Usage Data:</strong> Pages visited, time spent, interactions with features</li>
</ul>

<h2>2. How We Use Your Information</h2>
<ul>
<li>Processing and fulfilling your food orders</li>
<li>Providing order status updates and tracking</li>
<li>Improving our menu and services</li>
<li>Sending promotional offers (with your consent)</li>
<li>Website analytics and performance optimization</li>
<li>Ensuring security and preventing fraud</li>
</ul>

<h2>3. Cookies & Tracking</h2>
<p>We use cookies and similar technologies to enhance your browsing experience, remember your preferences, and analyze website traffic. We use Google AdSense for advertising, which may use cookies to serve relevant ads.</p>

<h2>4. Third-Party Services</h2>
<p>We use the following third-party services:</p>
<ul>
<li><strong>Google AdSense:</strong> For displaying advertisements</li>
<li><strong>Google Analytics:</strong> For website usage analytics</li>
<li><strong>VietQR:</strong> For generating payment QR codes</li>
<li><strong>Clerk:</strong> For admin authentication</li>
</ul>

<h2>5. Data Sharing</h2>
<p>We do not sell your personal information. We may share data with service providers who assist in operating our website and services, law enforcement when required by law, or business partners with your explicit consent.</p>

<h2>6. Data Retention</h2>
<p>We retain order data for business records and analytics. You may request deletion of your personal data by contacting us.</p>

<h2>7. Your Rights</h2>
<p>You have the right to access, correct, delete, or port your personal data. Contact us at the restaurant or via our website to exercise these rights.</p>

<h2>8. International Users</h2>
<p>Our services are operated from Vietnam. If you access our services from other countries, your data may be transferred to and processed in Vietnam.</p>

<h2>9. Contact Us</h2>
<p>For privacy-related inquiries, please visit us at Benz Eatery, Phu Quoc Island, Vietnam or reach out through our website.</p>
    `,
  },
  'terms': {
    title: 'Terms of Service',
    updated: 'February 2026',
    content: `
<h2>1. Acceptance of Terms</h2>
<p>By accessing and using the Benz Eatery website (benzeatery.com) and online ordering system, you agree to be bound by these Terms of Service.</p>

<h2>2. Online Ordering</h2>
<ul>
<li>Orders placed through our QR code system are binding once confirmed</li>
<li>Prices displayed on the menu are in Vietnamese Dong (VND) and include applicable taxes</li>
<li>We reserve the right to refuse or cancel orders at our discretion</li>
<li>Special dietary requirements should be communicated directly to staff</li>
</ul>

<h2>3. Payment</h2>
<ul>
<li>We accept Cash, Credit/Debit Cards, and Bank Transfer via VietQR</li>
<li>Card payments may include a processing fee as displayed at checkout</li>
<li>All payments must be settled before leaving the premises</li>
</ul>

<h2>4. Reservations & Bookings</h2>
<p>Attraction bookings and bike rental reservations made through our platform are subject to availability. Cancellation policies apply as stated during booking.</p>

<h2>5. Blog Content</h2>
<p>All blog content on Wander Vietnam (our travel guide) is for informational purposes only. We strive for accuracy but cannot guarantee all information is current. Travel conditions may change.</p>

<h2>6. Intellectual Property</h2>
<p>All content, images, logos, and materials on this website are the property of Benz Eatery and protected by copyright laws. You may not reproduce, distribute, or modify our content without prior written consent.</p>

<h2>7. User Conduct</h2>
<p>Users agree not to: attempt to hack, disrupt, or overload our systems; use automated tools to scrape our content; impersonate other users or staff; or engage in any fraudulent activity.</p>

<h2>8. Limitation of Liability</h2>
<p>Benz Eatery shall not be liable for any indirect, incidental, or consequential damages arising from your use of our website or services.</p>

<h2>9. Governing Law</h2>
<p>These terms are governed by the laws of the Socialist Republic of Vietnam. Any disputes shall be resolved in the courts of Kien Giang Province.</p>

<h2>10. Changes to Terms</h2>
<p>We may update these terms at any time. Continued use of our services constitutes acceptance of updated terms.</p>
    `,
  },
  'cookie-policy': {
    title: 'Cookie Policy',
    updated: 'February 2026',
    content: `
<h2>What Are Cookies?</h2>
<p>Cookies are small text files stored on your device when you visit our website. They help us provide a better user experience and understand how our site is used.</p>

<h2>Types of Cookies We Use</h2>

<h3>Essential Cookies</h3>
<p>Required for the website to function properly. These include session cookies for order processing, authentication tokens for staff/admin access, and security cookies.</p>

<h3>Analytics Cookies</h3>
<p>Help us understand how visitors interact with our website. We use Google Analytics to collect anonymous usage data including pages visited, time on site, and navigation paths.</p>

<h3>Advertising Cookies</h3>
<p>Google AdSense places cookies to serve relevant advertisements. These cookies track your browsing habits across websites to deliver personalized ads.</p>

<h3>Preference Cookies</h3>
<p>Remember your choices such as language preference, table number, and menu viewing settings.</p>

<h2>Managing Cookies</h2>
<p>You can control cookies through your browser settings:</p>
<ul>
<li><strong>Chrome:</strong> Settings &gt; Privacy and Security &gt; Cookies</li>
<li><strong>Firefox:</strong> Options &gt; Privacy &amp; Security &gt; Cookies</li>
<li><strong>Safari:</strong> Preferences &gt; Privacy &gt; Cookies</li>
</ul>
<p>Note: Disabling essential cookies may affect website functionality.</p>

<h2>Third-Party Cookies</h2>
<p>Third-party services (Google AdSense, Google Analytics) may set their own cookies. Please refer to their respective privacy policies for more information.</p>
    `,
  },
  'disclaimer': {
    title: 'Disclaimer',
    updated: 'February 2026',
    content: `
<h2>General Disclaimer</h2>
<p>The information provided on benzeatery.com is for general informational purposes only. While we strive to keep the information up to date and accurate, we make no representations or warranties of any kind about the completeness, accuracy, reliability, or suitability of the information.</p>

<h2>Menu & Pricing</h2>
<p>Menu items and prices are subject to change without notice. Images shown are for illustration purposes and may differ from actual dishes. Seasonal items may not always be available.</p>

<h2>Allergen Information</h2>
<p>While we make every effort to identify ingredients that may cause allergic reactions, we cannot guarantee that our dishes are free from allergens. Please inform our staff of any allergies before ordering.</p>

<h2>Travel Guide Content</h2>
<p>Our Wander Vietnam blog provides travel guides and recommendations based on our experience. Travel conditions, prices, and availability may change. Always verify current information before making travel plans.</p>

<h2>External Links</h2>
<p>Our website may contain links to external websites. We are not responsible for the content, privacy policies, or practices of third-party websites.</p>

<h2>Reviews & Ratings</h2>
<p>Ratings displayed from Google and TripAdvisor are sourced from their respective platforms and reflect user reviews at the time of display. We do not control or manipulate these ratings.</p>

<h2>Limitation</h2>
<p>In no event shall Benz Eatery be liable for any loss or damage arising from the use of this website or reliance on any information provided herein.</p>
    `,
  },
};

const PolicyPage = () => {
  const location = window.location.pathname;
  const slug = location.replace('/', '');
  const policy = POLICIES[slug];

  if (!policy) {
    return (
      <div className="plc-root">
        <div className="plc-container">
          <h1>Page Not Found</h1>
          <p>The requested policy page does not exist.</p>
          <Link to="/" className="plc-back">Back to Home</Link>
        </div>
        <style>{STYLES}</style>
      </div>
    );
  }

  return (
    <div className="plc-root" data-testid={`policy-${slug}`}>
      <nav className="plc-nav">
        <Link to="/" className="plc-logo">Benz Eatery</Link>
        <Link to="/" className="plc-back-link">Back to Site</Link>
      </nav>
      <div className="plc-container">
        <span className="plc-updated">Last updated: {policy.updated}</span>
        <h1>{policy.title}</h1>
        <div className="plc-body" dangerouslySetInnerHTML={{ __html: policy.content }} />
      </div>
      <footer className="plc-footer">
        <div className="plc-footer-links">
          {Object.entries(POLICIES).map(([key, val]) => (
            <Link key={key} to={`/${key}`} className={key === slug ? 'active' : ''}>{val.title}</Link>
          ))}
        </div>
        <p>© {new Date().getFullYear()} Benz Eatery · Phu Quoc, Vietnam</p>
      </footer>
      <style>{STYLES}</style>
    </div>
  );
};

const STYLES = `
  .plc-root {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    background: #fbf9f4; color: #1c1a17; min-height: 100vh;
    -webkit-font-smoothing: antialiased;
  }
  .plc-root a { color: inherit; text-decoration: none; }
  .plc-nav {
    max-width: 800px; margin: 0 auto; padding: 20px 32px;
    display: flex; justify-content: space-between; align-items: center;
  }
  .plc-logo { font-weight: 800; font-size: 18px; }
  .plc-back-link {
    font-size: 13px; color: #6b6660; font-weight: 500;
    padding: 6px 14px; border-radius: 8px; border: 1px solid rgba(0,0,0,0.1);
  }
  .plc-back-link:hover { background: rgba(0,0,0,0.03); }
  .plc-container { max-width: 800px; margin: 0 auto; padding: 20px 32px 60px; }
  .plc-updated {
    font-size: 12px; color: #9a9590; text-transform: uppercase;
    letter-spacing: 0.08em; font-weight: 600;
  }
  .plc-container h1 {
    font-family: Georgia, serif; font-size: 36px; font-weight: 500;
    margin: 12px 0 32px; line-height: 1.2;
  }
  .plc-body h2 {
    font-size: 20px; font-weight: 700; margin: 32px 0 12px;
    padding-top: 16px; border-top: 0.5px solid rgba(0,0,0,0.08);
  }
  .plc-body h3 { font-size: 16px; font-weight: 700; margin: 20px 0 8px; }
  .plc-body p { font-size: 15px; line-height: 1.7; color: #4a4540; margin-bottom: 12px; }
  .plc-body ul { padding-left: 20px; margin-bottom: 16px; }
  .plc-body li { font-size: 15px; line-height: 1.7; color: #4a4540; margin-bottom: 6px; }
  .plc-body strong { color: #1c1a17; }
  .plc-footer {
    max-width: 800px; margin: 0 auto; padding: 24px 32px 40px;
    border-top: 0.5px solid rgba(0,0,0,0.08); text-align: center;
  }
  .plc-footer-links {
    display: flex; gap: 16px; justify-content: center; flex-wrap: wrap;
    margin-bottom: 12px;
  }
  .plc-footer-links a {
    font-size: 13px; color: #6b6660; font-weight: 500;
  }
  .plc-footer-links a:hover { color: #da251d; }
  .plc-footer-links a.active { color: #da251d; font-weight: 700; }
  .plc-footer p { font-size: 12px; color: #9a9590; }
  @media (max-width: 640px) {
    .plc-container { padding: 16px 16px 40px; }
    .plc-container h1 { font-size: 28px; }
    .plc-nav { padding: 16px; }
  }
`;

export default PolicyPage;
